/**
 * CartItemQuantityControl
 *
 * Quantity editor with +/- buttons and remove.
 *
 * v1.15.x: Store API only (official Blocks flow)
 * - No AJAX fallback or update_checkout triggers during checkout
 * - Avoids calculate_totals() side effects that can duplicate items
 *
 * @package SuperPymeCheckoutBlocks
 * @since 1.11.9
 */

import { decodeEntities, escapeHtml } from '../utils/html';

const { useState, useCallback, useRef, useEffect } = wp.element;
const { useSelect, useDispatch } = wp.data;
const { speak } = wp.a11y;

const CART_STORE_KEY = 'wc/store/cart';
const CHECKOUT_STORE_KEY = 'wc/store/checkout';

/**
 * Get script data from multiple sources
 *
 * WC Blocks Integration passes data via wcSettings.getSetting()
 * wp_localize_script passes data via window.spcbQuantityData
 * We check both to ensure compatibility
 */
function getScriptData() {
    const fallbackData = {};

    if (window.spcbConfig) {
        if (window.spcbConfig.ajaxUrl) {
            fallbackData.ajaxUrl = window.spcbConfig.ajaxUrl;
        }
        if (window.spcbConfig.cartNonce) {
            fallbackData.nonce = window.spcbConfig.cartNonce;
        }
        if (window.spcbConfig.wcStoreApiUrl) {
            fallbackData.storeApiUrl = window.spcbConfig.wcStoreApiUrl;
        }
    }

    // Try wp_localize_script first
    if (window.spcbQuantityData && Object.keys(window.spcbQuantityData).length > 0) {
        return { ...fallbackData, ...window.spcbQuantityData };
    }

    // Try WC Blocks integration data
    if (typeof wcSettings !== 'undefined' && wcSettings.getSetting) {
        const wcData = wcSettings.getSetting('spcb-quantity-selector_data');
        if (wcData && Object.keys(wcData).length > 0) {
            return { ...fallbackData, ...wcData };
        }
    }

    // Fallback empty object
    console.warn('[SPCB] No quantity selector data found');
    return fallbackData;
}

const scriptData = getScriptData();
const {
    i18n,
    allowRemoveItems,
    enableQuantityEdit = true,
    debug = false,
    debounceMs = 400
} = scriptData;

/**
 * Session ID for correlating logs across requests
 */
const SESSION_ID = (() => {
    let id = sessionStorage.getItem('spcb_session');
    if (!id) {
        id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('spcb_session', id);
    }
    return id;
})();

/**
 * Log function - sends to server AND console
 *
 * v1.12.1: Always sends to server (regardless of WP_DEBUG) so we can
 * capture the timeline of events when the bug occurs in production.
 * Console logging only happens when debug mode is enabled.
 *
 * @param {string} action - The action being logged
 * @param {Object} data - Additional data to log
 */
function spcbLog(action, data) {
    const logData = {
        session: SESSION_ID,
        action,
        data: data || {},
        timestamp: Date.now()
    };

    // Console log only if debug is enabled
    if (debug) {
        console.log(`[SPCB][${SESSION_ID}][${Date.now()}]`, action, data);
    }

    // ALWAYS send to server (to capture events in production)
    try {
        const blob = new Blob([JSON.stringify(logData)], { type: 'application/json' });
        navigator.sendBeacon('/wp-json/spcb/v1/log', blob);
    } catch (e) {
        // Silent fallback if sendBeacon fails
        if (debug) {
            console.warn('[SPCB] Failed to send beacon:', e);
        }
    }
}

/**
 * Simple debounce hook (internal use only)
 *
 * @param {any} value
 * @param {number} delay
 * @returns {any}
 */
function useDebouncedValue(value, delay) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const handle = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(handle);
    }, [value, delay]);

    return debounced;
}

/**
 * Track the previous value across renders.
 *
 * @param {any} value
 * @returns {any}
 */
function usePrevious(value) {
    const ref = useRef();

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

/**
 * Expose global state for debugging
 */
if (typeof window !== 'undefined') {
    window.spcbDebugState = () => ({
        storeUpdateAvailable: (() => {
            try {
                const cartStore = window.wp?.data?.dispatch?.(CART_STORE_KEY);
                return typeof cartStore?.changeCartItemQuantity === 'function';
            } catch (e) {
                return false;
            }
        })()
    });
}

/**
 * Dispatch a Woo Blocks store event (for compatibility with extensions)
 *
 * @param {string} eventName
 * @param {Object} eventParams
 */
function dispatchStoreEvent(eventName, eventParams = {}) {
    try {
        window.wp?.hooks?.doAction?.(`experimental__woocommerce_blocks-${eventName}`, eventParams);
    } catch (e) {
        // Ignore failures (extension hook is optional)
    }
}

/**
 * Native quantity hook (Store API) modeled after Woo Blocks
 *
 * @param {Object} cartItem
 * @returns {Object}
 */
function useStoreCartItemQuantityNative(cartItem, options = {}) {
    const { autoUpdate = true } = options;
    const cartItemKey = typeof cartItem?.key === 'string' ? cartItem.key : '';
    const cartItemQuantity = typeof cartItem?.quantity === 'number' ? cartItem.quantity : 1;

    const [quantity, setQuantity] = useState(cartItemQuantity);
    const debouncedQuantity = useDebouncedValue(quantity, debounceMs);
    const previousDebouncedQuantity = usePrevious(debouncedQuantity);

    const { removeItemFromCart, changeCartItemQuantity } = useDispatch(CART_STORE_KEY);
    const { __internalIncrementCalculating, __internalDecrementCalculating } = useDispatch(CHECKOUT_STORE_KEY);

    useEffect(() => {
        setQuantity(cartItemQuantity);
    }, [cartItemQuantity]);

    const isPending = useSelect((select) => {
        if (!cartItemKey) {
            return { quantity: false, delete: false };
        }
        const store = select(CART_STORE_KEY);
        return {
            quantity: typeof store.isItemPendingQuantity === 'function'
                ? store.isItemPendingQuantity(cartItemKey)
                : false,
            delete: typeof store.isItemPendingDelete === 'function'
                ? store.isItemPendingDelete(cartItemKey)
                : false
        };
    }, [cartItemKey]);

    useEffect(() => {
        if (!autoUpdate) {
            return;
        }
        if (!cartItemKey) {
            return;
        }
        if (!Number.isFinite(previousDebouncedQuantity)) {
            return;
        }
        if (previousDebouncedQuantity === debouncedQuantity) {
            return;
        }

        if (window.spcbCheckoutLock?.isProcessing?.()) {
            spcbLog('store_qty_blocked', { reason: 'checkout_processing', cartItemKey, debouncedQuantity });
            return;
        }

        if (typeof changeCartItemQuantity !== 'function') {
            spcbLog('store_qty_missing', { reason: 'changeCartItemQuantity_missing', cartItemKey, debouncedQuantity });
            return;
        }

        changeCartItemQuantity(cartItemKey, debouncedQuantity).catch((error) => {
            spcbLog('store_qty_error', { cartItemKey, debouncedQuantity, error: error?.message });
        });
    }, [autoUpdate, cartItemKey, changeCartItemQuantity, debouncedQuantity, previousDebouncedQuantity]);

    useEffect(() => {
        if (typeof __internalIncrementCalculating !== 'function' || typeof __internalDecrementCalculating !== 'function') {
            return undefined;
        }

        if (isPending.delete) {
            __internalIncrementCalculating();
        } else {
            __internalDecrementCalculating();
        }

        return () => {
            if (isPending.delete) {
                __internalDecrementCalculating();
            }
        };
    }, [__internalDecrementCalculating, __internalIncrementCalculating, isPending.delete]);

    useEffect(() => {
        if (typeof __internalIncrementCalculating !== 'function' || typeof __internalDecrementCalculating !== 'function') {
            return undefined;
        }

        if (isPending.quantity || debouncedQuantity !== quantity) {
            __internalIncrementCalculating();
        } else {
            __internalDecrementCalculating();
        }

        return () => {
            if (isPending.quantity || debouncedQuantity !== quantity) {
                __internalDecrementCalculating();
            }
        };
    }, [__internalIncrementCalculating, __internalDecrementCalculating, isPending.quantity, debouncedQuantity, quantity]);

    const removeItem = useCallback(() => {
        if (cartItemKey) {
            return removeItemFromCart(cartItemKey).catch(() => false);
        }
        return Promise.resolve(false);
    }, [cartItemKey, removeItemFromCart]);

    return {
        quantity,
        setItemQuantity: setQuantity,
        removeItem,
        isPendingDelete: isPending.delete,
        isPendingQuantity: isPending.quantity
    };
}

/**
 * Show styled confirmation modal for removing a product
 *
 * @param {string} productName - Name of the product to remove
 * @returns {Promise<boolean>} - True if confirmed, false if cancelled
 */
function showRemoveConfirmModal(productName) {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'spcb-modal spcb-remove-confirm-modal';
        modal.innerHTML = `
            <div class="spcb-modal-overlay"></div>
            <div class="spcb-modal-content">
                <div class="spcb-modal-header">
                    <h3>Eliminar producto</h3>
                    <button type="button" class="spcb-modal-close" aria-label="Cerrar">&times;</button>
                </div>
                <div class="spcb-modal-body">
                    <div class="spcb-remove-confirm-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                    </div>
                    <p class="spcb-remove-confirm-message">¿Deseas eliminar <strong>${escapeHtml(productName)}</strong> del carrito?</p>
                </div>
                <div class="spcb-modal-footer">
                    <button type="button" class="spcb-modal-cancel">Cancelar</button>
                    <button type="button" class="spcb-modal-confirm spcb-modal-delete">Eliminar</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Focus the cancel button by default
        setTimeout(() => {
            modal.querySelector('.spcb-modal-cancel')?.focus();
        }, 50);

        const cleanup = (result) => {
            modal.remove();
            resolve(result);
        };

        // Event handlers
        modal.querySelector('.spcb-modal-overlay').addEventListener('click', () => cleanup(false));
        modal.querySelector('.spcb-modal-close').addEventListener('click', () => cleanup(false));
        modal.querySelector('.spcb-modal-cancel').addEventListener('click', () => cleanup(false));
        modal.querySelector('.spcb-modal-delete').addEventListener('click', () => cleanup(true));

        // Keyboard handling
        modal.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                cleanup(false);
            } else if (e.key === 'Enter' && e.target.classList.contains('spcb-modal-delete')) {
                cleanup(true);
            }
        });
    });
}

/**
 * CartItemQuantityControl Component
 *
 * Full quantity control with +/- buttons, input, and remove button.
 *
 * v1.13.x: Prefer Store API updates (Blocks-native), AJAX fallback when needed.
 *
 * @param {Object} props Component props
 * @param {Object} props.cartItem The cart item object
 * @param {string} props.itemKey The cart item key
 */
export default function CartItemQuantityControl({ cartItem, itemKey, showRemoveItemLink = true }) {
    const { createElement } = wp.element;

    // Read live cart item data so UI stays in sync with Store API updates.
    const liveCartItem = useSelect((select) => {
        const store = select(CART_STORE_KEY);
        if (!store) {
            return null;
        }

        const cartData = typeof store.getCartData === 'function'
            ? store.getCartData()
            : (typeof store.getCart === 'function' ? store.getCart() : null);

        const items = cartData?.items || [];
        const found = items.find((item) => item.key === itemKey);
        if (!found) {
            return null;
        }

        return {
            ...found,
            quantity_limits: found.quantity_limits ? { ...found.quantity_limits } : found.quantity_limits
        };
    }, [itemKey]);

    const activeCartItem = liveCartItem || cartItem;
    const displayName = decodeEntities(activeCartItem?.name || '');

    const {
        quantity: localQty,
        setItemQuantity,
        removeItem,
        isPendingDelete,
        isPendingQuantity
    } = useStoreCartItemQuantityNative(activeCartItem);

    const cartItemKey = activeCartItem?.key || itemKey;
    const [inputValue, setInputValue] = useState(String(localQty));
    const isEditingRef = useRef(false);

    useEffect(() => {
        if (!isEditingRef.current) {
            setInputValue(String(localQty));
        }
    }, [localQty]);

    const inputRef = useRef(null);

    // Get quantity limits
    const quantityLimits = activeCartItem.quantity_limits || {};
    const maxQty = quantityLimits.maximum || 9999;
    const minQty = quantityLimits.minimum || 1;
    const stepQty = quantityLimits.multiple_of || 1;
    const isQuantityEditable = quantityLimits.editable !== false && !activeCartItem.sold_individually;

    /**
     * Handle button click (+/-)
     *
     * Uses Store API updates with immediate feedback
     */
    const handleButtonClick = useCallback((delta) => {
        if (isPendingDelete || isPendingQuantity) {
            return;
        }

        const newQty = localQty + delta * stepQty;

        // Validate bounds
        if (newQty < minQty || newQty > maxQty) {
            return;
        }

        isEditingRef.current = false;
        setItemQuantity(newQty);
        setInputValue(String(newQty));
        dispatchStoreEvent('cart-set-item-quantity', {
            product: activeCartItem,
            quantity: newQty
        });
    }, [activeCartItem, cartItemKey, isPendingDelete, isPendingQuantity, localQty, maxQty, minQty, setItemQuantity, stepQty]);

    /**
     * Handle input change - just update local state, don't send to server yet
     */
    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setInputValue(value);
    }, []);

    const handleInputFocus = useCallback(() => {
        isEditingRef.current = true;
    }, []);

    /**
     * Handle input blur - commit the value via AJAX
     *
     * Commit input value to the Store API (or AJAX fallback)
     */
    const handleInputBlur = useCallback(() => {
        isEditingRef.current = false;
        const parsedValue = parseInt(inputValue, 10);
        const baseQty = Number.isFinite(parsedValue) ? parsedValue : localQty;
        const clampedQty = Math.max(minQty, Math.min(maxQty, baseQty));

        // Update local state to clamped value
        setItemQuantity(clampedQty);
        if (inputRef.current) {
            inputRef.current.value = String(clampedQty);
        }
        setInputValue(String(clampedQty));
        dispatchStoreEvent('cart-set-item-quantity', {
            product: activeCartItem,
            quantity: clampedQty
        });
    }, [activeCartItem, cartItemKey, inputValue, localQty, maxQty, minQty, setItemQuantity]);

    /**
     * Handle remove button click
     *
     * v1.12.2: Added logging for debugging
     * Note: Remove still uses Store API since it's straightforward and works
     */
    const handleRemove = useCallback(async () => {
        const productName = displayName || activeCartItem.name;

        spcbLog('item_remove_intent', {
            itemKey,
            productId: activeCartItem.id,
            productName,
            quantity: activeCartItem.quantity
        });

        // Show confirmation modal
        const confirmed = await showRemoveConfirmModal(productName);

        if (confirmed) {
            spcbLog('item_remove_confirmed', { itemKey, productName });
            try {
                await removeItem();
                dispatchStoreEvent('cart-remove-item', {
                    product: activeCartItem,
                    quantity: localQty
                });
                spcbLog('item_remove_success', { itemKey, productName });
                // Announce removal for screen readers
                const message = i18n?.removed
                    ? i18n.removed.replace('%s', productName)
                    : `${productName} eliminado del carrito`;
                speak(message);
            } catch (error) {
                spcbLog('item_remove_error', { itemKey, error: error.message });
                if (debug) {
                    console.error('[SPCB] Error removing item:', error);
                }
            }
        } else {
            spcbLog('item_remove_cancelled', { itemKey, productName });
        }
    }, [activeCartItem, itemKey, localQty, removeItem]);

    // Determine if control should be disabled
    const isDisabled = isPendingDelete || isPendingQuantity;

    // Build the control elements
    const elements = [];

    // Only show quantity controls if enabled
    if (enableQuantityEdit && isQuantityEditable) {
        // Minus button
        elements.push(
            createElement('button', {
                key: 'minus',
                type: 'button',
                className: 'spcb-qty-btn spcb-qty-minus wc-block-components-quantity-selector__button wc-block-components-quantity-selector__button--minus',
                onClick: () => handleButtonClick(-1),
                disabled: isDisabled || localQty <= minQty,
                'aria-label': i18n?.decrease || 'Reducir cantidad'
            }, createElement('svg', {
                width: 12,
                height: 12,
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 2
            }, createElement('line', { x1: 5, y1: 12, x2: 19, y2: 12 })))
        );

        // Quantity input - controlled input for proper React handling
        elements.push(
            createElement('input', {
                key: 'input',
                ref: inputRef,
                type: 'number',
                className: 'spcb-qty-input wc-block-components-quantity-selector__input',
                value: inputValue,
                min: minQty,
                max: maxQty,
                disabled: isDisabled,
                onChange: handleInputChange,
                onFocus: handleInputFocus,
                onBlur: handleInputBlur,
                onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleInputBlur();
                    }
                },
                'aria-label': i18n?.quantityOf
                    ? i18n.quantityOf.replace('%s', displayName || '')
                    : (i18n?.quantity || 'Cantidad')
            })
        );

        // Plus button
        elements.push(
            createElement('button', {
                key: 'plus',
                type: 'button',
                className: 'spcb-qty-btn spcb-qty-plus wc-block-components-quantity-selector__button wc-block-components-quantity-selector__button--plus',
                onClick: () => handleButtonClick(1),
                disabled: isDisabled || localQty >= maxQty,
                'aria-label': i18n?.increase || 'Aumentar cantidad'
            }, createElement('svg', {
                width: 12,
                height: 12,
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 2
            }, [
                createElement('line', { key: 'v', x1: 12, y1: 5, x2: 12, y2: 19 }),
                createElement('line', { key: 'h', x1: 5, y1: 12, x2: 19, y2: 12 })
            ]))
        );
    }

    // Remove button (always shown if allowed)
    if (allowRemoveItems && showRemoveItemLink) {
        elements.push(
            createElement('button', {
                key: 'remove',
                type: 'button',
                className: 'spcb-qty-remove wc-block-cart-item__remove-link',
                onClick: handleRemove,
                disabled: isDisabled,
                'aria-label': i18n?.remove || 'Eliminar del carrito',
                title: i18n?.remove || 'Eliminar'
            }, createElement('svg', {
                width: 14,
                height: 14,
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 2
            }, [
                createElement('polyline', { key: 'top', points: '3 6 5 6 21 6' }),
                createElement('path', { key: 'body', d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
                createElement('line', { key: 'l1', x1: 10, y1: 11, x2: 10, y2: 17 }),
                createElement('line', { key: 'l2', x1: 14, y1: 11, x2: 14, y2: 17 })
            ]))
        );
    }

    // Don't render if nothing to show
    if (elements.length === 0) {
        return null;
    }

    return createElement('div', {
        className: `spcb-qty-control-react wc-block-components-quantity-selector ${isDisabled ? 'is-loading' : ''}`
    }, elements);
}
